import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import IForgotPassword from "../../core/contracts/IForgotPassword";
import {
  CircularProgress,
  Card,
  InputLabel,
  FormControl,
  Input,
  FormHelperText,
} from "@material-ui/core";
import WMBack from "../../assets/images/wm_back.png";
import clsx from "clsx";
import WMLogoOneLiner from "../../assets/icons/WMLogoOneLiner";
import { isEmailValid } from "../../helpers/utility";

export interface IForgotPasswordPageProps {
  onSubmit: (this: void, form: IForgotPassword) => void;
  onCancel: (this: void) => void;
  loading?: boolean;
}

const useStyles = makeStyles((theme) => {
  const buttonAndLoadingSpacing = theme.spacing(3, 0, 2);

  return {
    main: {
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
      [theme.breakpoints.up("sm")]: { padding: theme.spacing(3) },
    },
    hiddenOverflow: {
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    divStyle: {
      width: "100%",
      height: "100vh",
      backgroundImage: `url(${WMBack})`,
      backgroundSize: "cover",
    },
    wmLogo: {
      [theme.breakpoints.down("sm")]: { width: "218px" },
      [theme.breakpoints.up("sm")]: { width: "418px" },
    },
    paper: {
      marginTop: theme.spacing(1),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      [theme.breakpoints.down("sm")]: { backgroundColor: "transparent" },
      [theme.breakpoints.up("sm")]: { backgroundColor: theme.palette.common.white },
      boxShadow: "none",
    },
    avatar: {
      margin: theme.spacing(1),
      backgroundColor: theme.palette.primary.main,
    },
    info: {
      marginTop: "20px",
      paddingBottom: 20,
      [theme.breakpoints.down("sm")]: { color: theme.palette.common.white },
      [theme.breakpoints.up("sm")]: { color: theme.palette.common.black },
    },
    form: {
      width: "100%", // Fix IE 11 issue.
      marginTop: theme.spacing(1),
    },
    textField: {
      width: "100%",
      paddingBottom: 10,
      [theme.breakpoints.down("sm")]: { color: theme.palette.common.white },
      [theme.breakpoints.up("sm")]: { color: theme.palette.common.black },
    },
    textPrimary: {
      marginTop: 20,
      fontSize: 24,
      fontWeight: "bold",
      fontVariant: "h5",
      [theme.breakpoints.down("sm")]: { color: theme.palette.common.white },
      [theme.breakpoints.up("sm")]: { color: theme.palette.common.black },
    },
    textSecond: {
      fontSize: 12,
      [theme.breakpoints.down("sm")]: { color: theme.palette.common.white, opacity: 1 },
      [theme.breakpoints.up("sm")]: { color: theme.palette.common.black, opacity: 0.5 },
    },
    textColorCommon: {
      [theme.breakpoints.down("sm")]: {
        color: theme.palette.common.white,
      },
      [theme.breakpoints.up("sm")]: {
        color: theme.palette.common.black,
      },
    },
    textLabelCommon: {
      [theme.breakpoints.down("sm")]: {
        "color": theme.palette.common.white,
        "&.Mui-focused": {
          color: theme.palette.common.white,
        },
      },
      [theme.breakpoints.up("sm")]: {
        "color": theme.palette.common.black,
        "&.Mui-focused": {
          color: theme.palette.primary.main,
        },
      },
    },
    buttonCommom: {
      borderRadius: 0,
      color: theme.palette.common.white,
    },
    submit_enviar: {
      margin: buttonAndLoadingSpacing,
      color: theme.palette.common.white,
      marginLeft: "4%",
      width: "48%",
    },
    submit_cancelar: {
      margin: buttonAndLoadingSpacing,
      color: theme.palette.common.white,
      width: "48%",
    },
    loading: {
      display: "flex",
      justifyContent: "center",
      padding: buttonAndLoadingSpacing,
    },
    section: {
      "& svg": {
        "& .fil0": {
          fill: theme.palette.common.white,
        },
      },
      "textAlign": "center",
    },
  };
});

const ForgotPasswordPage = (props: IForgotPasswordPageProps) => {
  const [formValues, setFormValues] = useState<IForgotPassword>({ email: "" });
  const [formError, setErrorTextField] = useState("E-mail obrigatório.");
  const [buttonEnable, setButtonEnable] = useState(false);

  const { onSubmit, onCancel, loading } = props;

  const handleInputChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });

    setButtonEnable(false);
    if (value === "") {
      setErrorTextField("E-mail obrigatório.");
    } else if (!isEmailValid(value)) {
      setErrorTextField("E-mail inválido.");
    } else {
      setErrorTextField("");
      setButtonEnable(true);
    }
  };

  const doReset = () => {
    onSubmit(formValues);
  };

  const doCancel = () => {
    onCancel();
  };

  const classes = useStyles();
  return (
    <section className={clsx(classes.hiddenOverflow, classes.divStyle)}>
      <section className={classes.section}>
        <WMLogoOneLiner className={classes.wmLogo} />
        <Container component="main" maxWidth="xs" className={classes.main}>
          <CssBaseline />
          <Card className={clsx(classes.paper, classes.main)}>
            <Typography component="h1" className={classes.textPrimary}>
              Esqueceu sua Senha?
            </Typography>
            <Typography component="h1" className={classes.info}>
              Informe seu email para que possamos enviar um link de redefinição de Senha
            </Typography>
            <form className={classes.form} noValidate>
              <FormControl className={classes.textField}>
                <InputLabel
                  htmlFor="standard-adornment-password"
                  className={classes.textLabelCommon}
                >
                  Email
                </InputLabel>
                <Input
                  autoComplete="off"
                  required
                  fullWidth
                  id="email"
                  name="email"
                  onChange={handleInputChange}
                  onKeyPress={(ev) => {
                    if (ev.key === "Enter") {
                      if (buttonEnable) {
                        onSubmit(formValues);
                      }
                      ev.preventDefault();
                    }
                  }}
                  autoFocus
                  className={classes.textColorCommon}
                />
                {formError && <FormHelperText id="form-errors">{formError}</FormHelperText>}
              </FormControl>
              {!loading ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    className={clsx(classes.submit_cancelar, classes.buttonCommom)}
                    onClick={doCancel}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    className={clsx(classes.submit_enviar, classes.buttonCommom)}
                    onClick={doReset}
                    disabled={!buttonEnable}
                  >
                    Enviar
                  </Button>
                </>
              ) : (
                <div className={classes.loading}>
                  <CircularProgress />
                </div>
              )}
            </form>
          </Card>
        </Container>
      </section>
    </section>
  );
};

export default ForgotPasswordPage;
